import React, { useCallback, useContext, useState } from "react";

import HighchartsComponent from "../HighchartsComponent";
import { useBrandConfigurator } from "../../contexts/BrandConfiguratorProvider";
import MeasuresContext from "../../contexts/Measures";

import { DownOutlined, UpOutlined } from "@ant-design/icons";

import * as Md from "../../md/full";
import "../../styles/brandModelAnalysis.css";
import _ from "lodash";
import { useAppContext } from "../../contexts/AppContext";
import { mode } from "../../utils/calculationUtils";

const VersionAnalysisReport = ({ filters, filterSelectors, ...props }) => {
    const widgetId = "version-analysis-chart";
    const { deselectConfigurator } = useBrandConfigurator();
    const { selectedKpi } = useContext(MeasuresContext);
    const { getCustlabelByCustId, getColorByCustId } = useAppContext();

    const { title, subtitle } = props;
    let selectedPlayers = props.selectedPlayers;

    if (!selectedPlayers) {
        const playerFilter = filters.find((obj) => obj.positiveAttributeFilter.displayForm.identifier === Md.CustId.attribute.displayForm.identifier);
        const playerCustIDs = playerFilter?.positiveAttributeFilter.in.values.length > 0 ? playerFilter.positiveAttributeFilter.in.values : ["OEM", "CAP"];
        selectedPlayers = playerCustIDs.map((custId) => ({
            custId: custId,
            custLabel: getCustlabelByCustId(custId),
            custColor: getColorByCustId(custId),
        }));
    }

    const dataVersionsFlat = Object.entries(filterSelectors)
        .reduce((acc, [brand, modelsObj]) => {
            const flatVersionsByModel = Object.entries(modelsObj)
                .reduce((accModel, [model, versionArr]) => {
                    const arr = versionArr.map((versionObj) => {
                        const custValues = Object.entries(versionObj).filter(([key, val]) => selectedPlayers.map((p) => p.custId).includes(key) && val);
                        const modeLp = mode(custValues.map(([key, kpiValues]) => kpiValues[1]));
                        return {
                            ...versionObj,
                            model: model,
                            y: _.min(custValues.map(([key, kpiValues]) => kpiValues[selectedKpi.index])),
                            listPrice: Number(modeLp) > 0 ? Math.round(Number(modeLp)) : null,
                        };
                    });
                    return [...accModel, ...arr];
                }, [])
                .map((obj) => ({
                    ...obj,
                    brand: brand,
                }));
            return [...acc, ...flatVersionsByModel];
        }, [])
        .sort((a, b) => a.y - b.y);

    const categories = dataVersionsFlat.map((elem) => elem.label);

    const series = [
        {
            name: "Cheapest",
            type: "bar",
            data: dataVersionsFlat.map((obj) => (obj.y > 0 ? selectedKpi.roundValue(obj.y) : null)),
            dataVersions: dataVersionsFlat,
            color: "#7189AA",
            yAxis: 0,
            enableMouseTracking: false,
            legendSymbol: "square",
        },
        ...selectedPlayers.map((p) => ({
            name: p.custLabel,
            type: "scatter",
            color: p.custColor,
            pointPlacement: props.showListPrices ? (categories.length < 7 ? pointPlacement[categories.length] : 0.22) : 0,
            allowPointSelect: false,
            // pointPlacement: props.showListPrices ? categories.length  > 6 ? 0.2 : pointPlacement[categories.length] : 0,
            showInLegend: dataVersionsFlat.filter((serie) => p.custId in serie && serie[p.custId] && serie[p.custId][selectedKpi.index] > 0).length > 0,
            legend: {
                symbolRadius: 0,
            },
            data: dataVersionsFlat.map((obj, i) =>
                p.custId in obj && obj[p.custId]
                    ? {
                          x: i,
                          y: selectedKpi.roundValue(obj[p.custId][selectedKpi.index]),
                      }
                    : null,
            ),
        })),
        {
            name: "List Price",
            type: "bar",
            maxPointWidth: 18,
            yAxis: 1,
            data: dataVersionsFlat.map((obj) => (obj.listPrice > 0 ? obj.listPrice : null)),
            allowPointSelect: false,
            events: {
                legendItemClick: function () {
                    return false;
                },
                click: null,
            },
            color: "#FF7D93",
            enableMouseTracking: false,
            legend: {
                symbolRadius: 0,
                symbolWidth: 0,
                squareSymbol: false,
            },
            dataLabels: {
                enabled: true,
                inside: true,
                align: "right",
            },
            showInLegend: props.showListPrices,
            visible: props.showListPrices,
        },
    ];

    // .map((val) => selectedKpi.roundValue(val));

    const showFullGraph = categories.length <= 8;

    const onClickBar = useCallback(
        (event, text) => {
            if (props.widgetUid) return;
            if (event?.target.textContent || event.point?.series.name === "Cheapest") {
                const versionObj = dataVersionsFlat.find((item) => item.label === text);
                if (versionObj) deselectConfigurator(versionObj.brand, versionObj.model, [versionObj]);
            }
        },
        [filterSelectors, dataVersionsFlat, props],
    );

    const report = <VersionAnalysisChart filters={filters} categories={categories} title={title} subtitle={subtitle} onClickBar={onClickBar} series={series} widgetId={widgetId} {...props} />;
    return props.widgetUid ? report : <VersionAnalysisWrapper showFullGraph={showFullGraph} reportComponent={report} />;
};

const VersionAnalysisWrapper = ({ reportComponent, showFullGraph, ...props }) => {
    const [showMore, setShowMore] = useState(false);
    return (
        <div>
            <div
                className={!showFullGraph && !showMore ? "h-[400px] overflow-hidden bg-white" : ""}
                style={
                    !showFullGraph && !showMore
                        ? {
                              maskImage: "linear-gradient(to bottom, black 70%, transparent 100%)",
                              WebkitMaskImage: "linear-gradient(to bottom, black 70%, transparent 100%)",
                          }
                        : {}
                }
            >
                {reportComponent}
                {props.footnote && <span className="pl-3 pb-2 text-left text-xs font-medium">{props.footnote}</span>}
            </div>

            {!showFullGraph && (
                <div
                    className={!showMore ? "mx-auto max-w-fit flex items-center justify-center gap-2 px-4 py-1.5 mb-2 rounded-md hover:bg-backgroundColor cursor-pointer" : "mx-auto max-w-fit flex items-center justify-center mb-2 p-2 rounded-full hover:bg-backgroundColor"}
                    onClick={() => setShowMore(!showMore)}
                >
                    {!showMore ? (
                        <>
                            <DownOutlined
                                // className={"p-2 rounded-full hover:bg-backgroundColor"}
                                style={{
                                    color: "#506e96",
                                }}
                            />
                            <span className="text-steel font-semibold">Show versions</span>
                        </>
                    ) : (
                        <UpOutlined
                            style={{
                                color: "#506e96",
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

const VersionAnalysisChart = ({ filters, categories, title, subtitle, series, onClickBar, ...props }) => {
    return (
        <HighchartsComponent
            key={JSON.stringify(series)}
            widgetProps={{
                id: props.widgetId,
                filters,
                section: "oem-views",
            }}
            options={{
                chart: {
                    type: "bar",
                    height: categories.length > 6 ? categories.length * 75 : props.height,
                    marginLeft: props.widgetUid ? 270 : 480,
                },
                plotOptions: {
                    scatter: {
                        stickyTracking: false,
                        showInLegend: false,
                        // findNearestPointBy: 'y',
                        marker: {
                            radius: 5,
                            symbol: "circle",
                        },
                    },
                    bar: {
                        pointWidth: 30,
                        borderRadius: 2,
                        dataLabels: {
                            enabled: true,
                        },
                        point: {
                            events: {
                                cursor: "pointer",
                                click: function (event) {
                                    onClickBar(event, event.point.category.name);
                                },
                            },
                        },
                    },
                    series: {
                        grouping: true,
                        groupPadding: categories.length < 8 ? 0.45 - categories.length * 0.05 : 0.05,
                        pointPadding: 0,
                    },
                },
                title: {
                    text: title,
                    align: "center",
                    style: {
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#3a0ca3",
                    },
                },
                subtitle: {
                    text: subtitle,
                    align: "center",
                    style: {
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#506e96",
                    },
                },
                xAxis: {
                    categories: categories,
                    labels: {
                        // padding: 3,
                        style: {
                            textOverflow: "ellipsis",
                            // whiteSpace: 'nowrap',
                            overflow: "ellipsis",
                        },
                        useHTML: true,
                        formatter: function () {
                            return props.widgetUid ? this.value : `<span class="versionxAxisLabel">${this.value}</span>`;
                        },
                        events: {
                            click: function (event) {
                                onClickBar(event, event.target.textContent);
                            },
                        },
                    },
                },
                yAxis: [
                    {
                        title: {
                            text: null,
                        },
                        tickPosition: "start",
                        offset: 0,
                    },
                    {
                        title: {
                            text: null,
                        },
                        labels: {
                            enabled: false,
                        },
                        type: "logarithmic",
                        opposite: true,
                        tickInterval: 1.5,
                    },
                ],
                tooltip: {
                    formatter: function () {
                        return `${this.x.name}<br/>${this.series.name} <b>${this.y}</b>`;
                    },
                },
                series: series,
                legend: {
                    symbolRadius: 1,
                },
            }}
            menuButtonPosition={props.widgetUid ? "outside" : "inside"}
            {...props}
        />
    );
};

const pointPlacement = {
    1: 0.05,
    2: 0.08,
    3: 0.1,
    4: 0.12,
    5: 0.15,
    6: 0.16,
};
export default VersionAnalysisReport;
